import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Brain,
    BarChart3,
    Heart,
    Users,
    TrendingUp,
    Shield,
    Mic,
    MessageSquare,
    ChevronRight,
    Sparkles,
    ArrowRight,
    CheckCircle2
} from 'lucide-react';

const features = [
    {
        icon: BarChart3,
        title: 'Sentiment Analysis',
        description: 'Real-time analysis of employee communications to understand attitudes and engagement levels.',
    },
    {
        icon: Brain,
        title: 'Psychometric Profiling',
        description: 'AI-powered personality insights using Big Five and DISC models for better team dynamics.',
    },
    {
        icon: Heart,
        title: 'Well-being Monitoring',
        description: 'Track stress, burnout indicators, and emotional health with personalized recommendations.',
    },
    {
        icon: TrendingUp,
        title: 'Predictive Analytics',
        description: 'Early warning systems for attrition risk, disengagement, and leadership potential.',
    },
    {
        icon: Mic,
        title: 'Voice Analysis',
        description: 'Analyze tone, sentiment, and behavioral markers from meetings and interviews.',
    },
    {
        icon: Users,
        title: 'Team Compatibility',
        description: 'Assess and improve team dynamics with compatibility indices and collaboration insights.',
    },
];

const benefits = [
    'Reduce employee turnover by up to 35%',
    'Identify burnout risk before it impacts performance',
    'Build high-performing teams with compatibility insights',
    'Data-driven HR decisions backed by AI',
];

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-[#FAFAF9]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/60">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shadow-glow">
                            <Brain className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xl font-bold font-heading text-stone-900">EmpathIQ</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/login">
                            <Button variant="ghost" className="btn-ghost" data-testid="nav-login-btn">
                                Sign In
                            </Button>
                        </Link>
                        <Link to="/register">
                            <Button className="rounded-full px-6 bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/20" data-testid="nav-register-btn">
                                Get Started
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-6 gradient-hero">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="lg:col-span-7"
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full text-indigo-600 text-sm font-medium mb-6">
                                <Sparkles className="w-4 h-4" />
                                AI-Powered Human Capital Intelligence
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-stone-900 leading-tight mb-6">
                                Transform Employee Data into{' '}
                                <span className="text-gradient">Actionable Intelligence</span>
                            </h1>
                            <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-2xl">
                                EmpathIQ combines sentiment analysis, psychometric profiling, and well-being monitoring to 
                                empower organizations with predictive HR intelligence. Build a thriving, engaged workforce.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/register">
                                    <Button className="btn-primary text-lg px-8 py-6" data-testid="hero-cta-btn">
                                        Start Free Trial
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                                <Button variant="outline" className="btn-secondary text-lg px-8 py-6" data-testid="hero-demo-btn">
                                    Watch Demo
                                </Button>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="lg:col-span-5"
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/20 to-coral-DEFAULT/20 rounded-3xl blur-3xl" />
                                <img
                                    src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwb2ZmaWNlJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBicmlnaHR8ZW58MHx8fHwxNzY3NDU3NjcxfDA&ixlib=rb-4.1.0&q=85"
                                    alt="Team collaboration"
                                    className="relative rounded-3xl shadow-2xl border border-stone-200"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Benefits Strip */}
            <section className="py-6 bg-indigo-600">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-wrap justify-center gap-8">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="flex items-center gap-2 text-white"
                            >
                                <CheckCircle2 className="w-5 h-5 text-indigo-200" />
                                <span className="text-sm font-medium">{benefit}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold font-heading text-stone-900 mb-4">
                            Comprehensive HR Intelligence
                        </h2>
                        <p className="text-lg text-stone-500 max-w-2xl mx-auto">
                            From sentiment analysis to predictive analytics, EmpathIQ provides everything you need
                            to understand and optimize your workforce.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className="h-full bento-card-interactive group">
                                    <CardContent className="p-6">
                                        <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-4 group-hover:bg-indigo-100 transition-colors">
                                            <feature.icon className="w-6 h-6 text-indigo-600" />
                                        </div>
                                        <h3 className="text-lg font-semibold font-heading text-stone-900 mb-2">
                                            {feature.title}
                                        </h3>
                                        <p className="text-stone-500 text-sm leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24 px-6 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold font-heading text-stone-900 mb-4">
                            How EmpathIQ Works
                        </h2>
                        <p className="text-lg text-stone-500 max-w-2xl mx-auto">
                            Transform raw data into actionable insights in four simple steps.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {[
                            { step: '01', title: 'Collect', desc: 'Gather data from emails, surveys, meetings, and HR systems.' },
                            { step: '02', title: 'Analyze', desc: 'AI processes text and voice for sentiment and personality.' },
                            { step: '03', title: 'Predict', desc: 'ML models forecast risks and identify opportunities.' },
                            { step: '04', title: 'Act', desc: 'Receive actionable recommendations and interventions.' },
                        ].map((item, index) => (
                            <motion.div
                                key={item.step}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-5xl font-bold font-heading text-indigo-100 mb-4">
                                    {item.step}
                                </div>
                                <h3 className="text-xl font-semibold font-heading text-stone-900 mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-stone-500 text-sm">
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        className="relative overflow-hidden rounded-3xl bg-indigo-600 p-12 text-center"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-indigo-700" />
                        <div className="absolute inset-0 opacity-10">
                            <svg className="w-full h-full" viewBox="0 0 400 400">
                                <defs>
                                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                                    </pattern>
                                </defs>
                                <rect width="100%" height="100%" fill="url(#grid)" />
                            </svg>
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mb-4">
                                Ready to Transform Your HR Strategy?
                            </h2>
                            <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
                                Join leading organizations using EmpathIQ to build engaged, high-performing teams.
                            </p>
                            <Link to="/register">
                                <Button className="bg-white text-indigo-600 hover:bg-indigo-50 rounded-full px-8 py-6 text-lg font-semibold shadow-xl" data-testid="cta-get-started-btn">
                                    Get Started Free
                                    <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 border-t border-stone-200 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                                <Brain className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-lg font-bold font-heading text-stone-900">EmpathIQ</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-stone-500">
                            <Link to="#" className="hover:text-stone-900 transition-colors">Privacy Policy</Link>
                            <Link to="#" className="hover:text-stone-900 transition-colors">Terms of Service</Link>
                            <Link to="#" className="hover:text-stone-900 transition-colors">Contact</Link>
                        </div>
                        <p className="text-sm text-stone-400">
                            © 2024 EmpathIQ. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
